import { ASK_TYPES, askPath, type AskType } from '@arthelper/shared/community-ask';

/**
 * Every one-click link the product understands: a URL an email, a post or a DM can carry
 * that DOES something on landing (opens a form, prefills it, posts, joins, starts a chat),
 * as opposed to a link that only shows a page.
 *
 * Paths and params here describe parsers that live elsewhere; each entry names its parser in
 * `source` so a change to the behaviour is easy to carry back here. The /ask entries are
 * built from `askPath()`, the same function emails use, so they cannot drift.
 */

/** Which app answers the link. `community` is town-square (arthelper.com); `app` is this client. */
export type LinkHost = 'community' | 'app';

export interface LinkExample {
  label: string;
  /** Path plus query, joined to the host's origin at render time. */
  path: string;
}

export interface OneClickLink {
  id: string;
  title: string;
  host: LinkHost;
  /** The URL shape, with {placeholders}. */
  pattern: string;
  /** What happens when someone clicks it. */
  does: string;
  /** Signed-out behaviour, and anything else a sender must know. */
  notes?: string;
  params?: Array<{ name: string; values: string }>;
  examples: LinkExample[];
  /** The file that parses the link — where to look when the behaviour changes. */
  source: string;
}

export interface LinkGroup {
  id: string;
  title: string;
  blurb: string;
  links: OneClickLink[];
}

export const ASK_LABELS: Record<AskType, string> = {
  rating: 'Rate my art',
  pricing: 'Price check',
  critique: 'Critique',
  'which-is-better': 'Which is better?',
};

const ASK_WHAT: Record<AskType, string> = {
  rating: 'a Rate my Art post (community scores the piece 1–10), prefilled with the piece and its description',
  pricing: 'a pricing-feedback post, prefilled with the piece, its description and an AI-suggested price range',
  critique:
    'a critique request, prefilled with the piece, its title/medium/size and an AI-written "what I want feedback on" in the artist\'s voice',
  'which-is-better': 'a two-image poll ("Which version is stronger?") with the pieces as Version A and Version B',
};

const SIGN_IN_RETURNS = 'Signed out: signs in, then comes straight back to the same link.';

function askLinks(type: AskType): OneClickLink[] {
  const pair = type === 'which-is-better';
  const ids = pair ? ['123', '456'] : ['123'];
  const target = pair ? '{idA},{idB}' : '{imageId}';
  const surpriseWhat = pair ? 'two random recent public pieces' : 'a random recent public piece';
  return [
    {
      id: `ask-${type}-compose`,
      title: `${ASK_LABELS[type]} — open the form, filled in`,
      host: 'community',
      pattern: `/ask/${type}/{surprise-me | ${target} | choose}`,
      does: `Opens ${ASK_WHAT[type]} on the General community, ready for the artist to review and post. "surprise-me" picks ${surpriseWhat}; "choose" asks the artist to pick ${pair ? 'both versions' : 'the piece'} first, then fills in the rest.`,
      notes: SIGN_IN_RETURNS,
      examples: [
        { label: 'Surprise me', path: askPath(type) },
        { label: pair ? 'Two specific pieces (A, B)' : 'A specific piece', path: askPath(type, ids) },
        { label: pair ? 'Artist picks both' : 'Artist picks the piece', path: askPath(type, [], { choose: true }) },
      ],
      source: 'shared/community-ask.ts · town-square/src/components/community/ask-link/AskLinkLauncher.tsx',
    },
    {
      id: `ask-${type}-post`,
      title: `${ASK_LABELS[type]} — confirm and post in one click`,
      host: 'community',
      pattern: `/ask/${type}/{surprise-me | ${target}}/post`,
      does: `Opens a prefilled confirm screen for ${ASK_WHAT[type]} in General, one click to publish. Publish lands on the live post with "Your post is live! Feedback should start coming in within 24 hours."; Edit opens the prefilled form, Cancel posts nothing.`,
      notes: `${SIGN_IN_RETURNS} Nothing is posted until the artist clicks Publish — opening, reloading or pre-fetching the link (mail scanners included) never posts, and the screen cannot be framed by another site. Publishing again within 2 minutes lands on the same post instead of posting twice.`,
      examples: [
        { label: 'Surprise me', path: askPath(type, [], { post: true }) },
        { label: pair ? 'Two specific pieces (A, B)' : 'A specific piece', path: askPath(type, ids, { post: true }) },
      ],
      source: 'town-square/src/components/community/ask-link/AskConfirmPost.tsx',
    },
  ];
}

export const LINK_GROUPS: LinkGroup[] = [
  {
    id: 'asks',
    title: 'Ask the community for feedback',
    blurb:
      'Get an artist posting a feedback ask in General. Each type has a "fill the form" link and a "confirm and post" link (a prefilled confirm screen, one click to publish); the builder above makes them for any piece.',
    links: ASK_TYPES.flatMap(askLinks),
  },
  {
    id: 'assistant',
    title: 'Start an Arty (assistant) conversation',
    blurb: 'Sparklinks: drop the reader into Arty with a message already sent.',
    links: [
      {
        id: 'sparklink',
        title: 'Sparklink — open Arty and send a message',
        host: 'community',
        pattern: '/community?message=…&image_id=…&images=…&context=…&spark_id=…&chat_destination=assistant',
        does: 'Opens the Arty panel in a new chat and sends the message (with the pieces attached). With chat_destination=assistant it opens the full Assistant page instead.',
        notes: `${SIGN_IN_RETURNS} Any page works; non-community pages forward to /community first.`,
        params: [
          { name: 'message', values: 'text Arty receives (≤ 8 KB)' },
          { name: 'image_id', values: 'up to 5 My Artwork ids, comma-separated' },
          { name: 'images', values: 'up to 5 https image URLs, comma-separated' },
          { name: 'context', values: 'hidden instructions for Arty (≤ 8 KB)' },
          { name: 'spark_id', values: 'a spark to run' },
          { name: 'chat_destination', values: '"assistant" for the full-page Assistant' },
        ],
        examples: [
          {
            label: 'Critique a specific piece',
            path: '/community?message=Give%20me%20an%20honest%20critique%20of%20this%20piece.&image_id=123',
          },
          {
            label: 'Open in the full Assistant',
            path: '/community?message=Help%20me%20price%20my%20work&chat_destination=assistant',
          },
        ],
        source: 'shared/sparklink.ts · town-square/src/components/arty/ArtyPanelProvider.tsx',
      },
      {
        id: 'sparklink-upload',
        title: 'Sparklink + upload prompt',
        host: 'community',
        pattern: '/community?display_upload_modal=true&modal_header_text=…&modal_header_subtext=…',
        does: 'Opens the uploader first with your headline; any sparklink message waits until the upload finishes, then runs on the new piece.',
        params: [
          { name: 'force_uploader_on_all_users', values: 'true — show even to artists who have uploads' },
          { name: 'force_uploader_when_zero_uploads_only', values: 'true — only artists with no uploads' },
        ],
        examples: [
          {
            label: 'Upload then critique',
            path: '/community?display_upload_modal=true&modal_header_text=Show%20us%20your%20latest&message=Critique%20this%20piece',
          },
        ],
        source: 'town-square/src/lib/artySparklinkUploadModal.ts',
      },
      {
        id: 'public-assistant',
        title: 'Public Assistant chat (works signed out)',
        host: 'app',
        pattern: '/assistant?message=…&images=…&context=…&spark_id=…',
        does: 'Starts a new Assistant chat and sends the message once — no account needed. Signing up later carries the chat over.',
        examples: [
          {
            label: 'Ask a question',
            path: '/assistant?message=How%20should%20I%20price%20a%2016x20%20oil%20painting%3F',
          },
        ],
        source: 'client/src/hooks/usePublicChat.ts',
      },
      {
        id: 'chat-message',
        title: 'Signed-in chat with a message',
        host: 'app',
        pattern: '/chat?message=…&spark_id=… · /art/{id}/chat?message=…',
        does: 'Opens a chat (optionally about one piece) and sends the message. display_upload_modal=true opens the uploader first.',
        examples: [{ label: 'Chat about a piece', path: '/art/123/chat?message=Write%20a%20description%20for%20this' }],
        source: 'client/src/hooks/useConversationChat.ts',
      },
    ],
  },
  {
    id: 'create',
    title: 'Open a composer, uploader or modal',
    blurb: 'Land the reader with the right box already open.',
    links: [
      {
        id: 'compose',
        title: 'Community post composer, prefilled',
        host: 'community',
        pattern: '/community?compose=1&body=…',
        does: 'Expands the feed composer with the body text already typed (used by "Share a WIP" to @mention someone).',
        notes: 'Signed in only.',
        examples: [{ label: 'Compose with a mention', path: '/community?compose=1&body=%40jane' }],
        source: 'town-square/src/components/community/CommunityFeedComposer.tsx',
      },
      {
        id: 'upload',
        title: 'Open my uploader',
        host: 'community',
        pattern: '/{yourProfileSlug}?upload=1',
        does: "Opens the gallery uploader on the artist's own profile.",
        notes: 'Only works on your own profile slug — it is a personal link.',
        examples: [{ label: 'Upload (profile "jane")', path: '/jane?upload=1' }],
        source: 'town-square/src/components/profile/GalleryUploadProvider.tsx',
      },
      {
        id: 'invite-modal',
        title: 'Invite-a-friend modal',
        host: 'community',
        pattern: '/{any page}?invite=1',
        does: 'Opens the invite-friends modal on whatever page it lands on (also works on app.arthelper.ai).',
        notes: SIGN_IN_RETURNS,
        examples: [{ label: 'Invite from the feed', path: '/community?invite=1' }],
        source: 'town-square/src/components/InviteModalProvider.tsx',
      },
      {
        id: 'welcome-modal',
        title: 'Custom welcome modal',
        host: 'community',
        pattern: '/community?w=1&t=…&ta=…&l=…&o=…&cta=…&k=…&wbg=…&wfg=…&wa=…&c=1',
        does: 'Shows a branded welcome modal with your title, text and button. c=1 adds confetti; colours are 6-digit hex without #.',
        examples: [{ label: 'Welcome with confetti', path: '/community?w=1&t=Welcome!&ta=Glad%20you%27re%20here&c=1' }],
        source: 'town-square/src/lib/welcomeModal.ts',
      },
      {
        id: 'wizard',
        title: 'Feature wizard',
        host: 'community',
        pattern: '/{any page}?wizard={slug}',
        does: 'Opens a feature wizard walkthrough (slugs are managed under Admin → Feature wizards). Also works on app.arthelper.ai.',
        notes: `Needs the feature_wizards flag on. ${SIGN_IN_RETURNS}`,
        examples: [{ label: 'Open a wizard', path: '/community?wizard=price-check' }],
        source: 'town-square/src/hooks/useFeatureWizard.ts',
      },
      {
        id: 'instagram-post',
        title: 'Instagram post flow, prefilled',
        host: 'app',
        pattern: '/post?image_id={id}&caption=…&initial_step=1',
        does: 'Opens the Instagram post builder with the piece and caption already in, starting at the given step.',
        examples: [{ label: 'Post a piece', path: '/post?image_id=123&caption=New%20work!&initial_step=1' }],
        source: 'client/src/context/post.tsx',
      },
      {
        id: 'new-dm',
        title: 'New direct message to someone',
        host: 'app',
        pattern: '/messages/new?to={asfId}&name=…',
        does: 'Opens a new DM with the recipient already chosen.',
        examples: [{ label: 'Message a member', path: '/messages/new?to=1000003&name=Jane' }],
        source: 'client/src/pages/messages/new.tsx',
      },
    ],
  },
  {
    id: 'join',
    title: 'Join, accept and take part',
    blurb: 'Invites and participation links. Tokens come from the invite systems, so these are generated per person.',
    links: [
      {
        id: 'community-invite',
        title: 'Accept a community invite',
        host: 'community',
        pattern: '/community/join?token={token}',
        does: 'Accepts the invite automatically and lands in the community.',
        notes: `${SIGN_IN_RETURNS} Tokens: Admin → Community invites.`,
        examples: [{ label: 'Invite link shape', path: '/community/join?token=TOKEN' }],
        source: 'town-square/src/app/[locale]/community/join/page.tsx',
      },
      {
        id: 'guild-invite',
        title: 'Guild invite',
        host: 'community',
        pattern: '/guilds/{slug}?join={token}',
        does: 'Shows the guild with a one-tap Join button for that invite; signed-out visitors keep the invite through sign-up.',
        examples: [{ label: 'Invite link shape', path: '/guilds/plein-air-crew?join=TOKEN' }],
        source: 'town-square/src/lib/guild-invite-auth.ts',
      },
      {
        id: 'guild-tab',
        title: 'Guild tab / share checklist',
        host: 'community',
        pattern: '/guilds/{slug}?tab={start|chat|activity|members|about}&share=1',
        does: 'Opens the guild on a tab; share=1 on the Start tab opens the share picker.',
        examples: [{ label: 'Guild chat', path: '/guilds/plein-air-crew?tab=chat' }],
        source: 'town-square/src/app/[locale]/guilds/[slug]/_guild/GuildPage.tsx',
      },
      {
        id: 'quest-join',
        title: 'Join a quest',
        host: 'community',
        pattern: '/quests/{slug}?join=1 · /quests/{slug}?questInvite={token}',
        does: 'Joins the quest automatically (join=1), or accepts a private quest invite (questInvite).',
        notes: 'Signed out: shows the quest landing with sign-up, keeping the link.',
        examples: [{ label: 'Join a quest', path: '/quests/spring-sprint?join=1' }],
        source: 'town-square/src/app/[locale]/quests/[slug]/QuestLandingClient.tsx',
      },
      {
        id: 'bounty-open',
        title: 'Open a bounty',
        host: 'community',
        pattern: '/bounties/{slug}?open=1',
        does: "Opens the bounty's action panel (when the artist has joined and it isn't finished).",
        examples: [{ label: 'Open a bounty', path: '/bounties/first-critique?open=1' }],
        source: 'town-square/src/app/[locale]/bounties/[slug]/BountyPageClient.tsx',
      },
      {
        id: 'gift-invite',
        title: 'Gift / referral invite',
        host: 'community',
        pattern: '/invite?invite={referralHash}&gift={token}',
        does: 'Shows the personalised gift landing and keeps the gift for 30 days through sign-up.',
        examples: [{ label: 'Gift link shape', path: '/invite?invite=HASH&gift=TOKEN' }],
        source: 'town-square/src/lib/gift-invite-stash.ts',
      },
    ],
  },
  {
    id: 'answer',
    title: 'Answer or jump to a conversation',
    blurb: 'Links that land on a post or artwork with an answer or a comment ready.',
    links: [
      {
        id: 'digest-answer',
        title: 'Pre-pick a rating or price (digest buttons)',
        host: 'community',
        pattern: '/community/{community}/post/{post}?rating={1-10} · ?price={dollars}',
        does: 'Opens a Rate my Art or pricing post with the tapped answer already selected. The reader still taps Submit — nothing is recorded on landing.',
        examples: [{ label: 'Rate a post 7', path: '/community/general/post/POST-SLUG?rating=7' }],
        source: 'town-square/src/components/community/post-types/digest-deep-link.ts',
      },
      {
        id: 'post-comment',
        title: 'Jump to a post comment',
        host: 'community',
        pattern: '/community/{community}/post/{post}?c={commentId}',
        does: 'Loads the post and scrolls to that comment.',
        examples: [{ label: 'Comment link shape', path: '/community/general/post/POST-SLUG?c=55' }],
        source: 'town-square/src/components/community/CommunityCommentDeepLink.tsx',
      },
      {
        id: 'artwork-comments',
        title: 'Jump to artwork comments',
        host: 'community',
        pattern: '/{artist}/image/{piece}?focus=comments · ?c={commentId}',
        does: 'Opens a piece and focuses its comment box (or a specific comment) — used to nudge people to comment.',
        examples: [{ label: 'Comment on a piece', path: '/jane/image/sunset?focus=comments' }],
        source: 'town-square/src/components/image-comments/comment-deep-link.ts',
      },
    ],
  },
  {
    id: 'email',
    title: 'Email preferences',
    blurb: 'Generated per recipient by the email jobs — listed so you know what they do.',
    links: [
      {
        id: 'unsubscribe',
        title: 'Unsubscribe',
        host: 'app',
        pattern: '/unsubscribe?token={token}&type={emailType}',
        does: 'Unsubscribes immediately on landing — no button. Types: activity, copyright, vote, trending, followPost, digest, dailyTrendingDigest, inactivityDigest, communityNotification, connection, dm, recommendedConnections.',
        notes: 'Tokens are signed per user; never hand-build one.',
        examples: [{ label: 'Link shape', path: '/unsubscribe?token=TOKEN&type=digest' }],
        source: 'client/src/pages/unsubscribe.tsx',
      },
      {
        id: 'digest-mute',
        title: 'Mute a community in the digest',
        host: 'app',
        pattern: '/community/digest-mute?token={token}[&action=undo]',
        does: 'Asks to confirm, then mutes (or with action=undo, unmutes) that community in the daily digest.',
        examples: [{ label: 'Link shape', path: '/community/digest-mute?token=TOKEN' }],
        source: 'client/src/pages/community-digest-mute.tsx',
      },
      {
        id: 'settings-notifications',
        title: 'Notification settings',
        host: 'app',
        pattern: '/settings?tab=notifications',
        does: 'Opens notification settings (every email footer links here).',
        examples: [{ label: 'Notification settings', path: '/settings?tab=notifications' }],
        source: 'client/src/pages/settings',
      },
    ],
  },
];
